import React, { FC, useEffect, useState } from "react";
import { Box, Stack } from "@mui/system";
import { graphql, Link, useStaticQuery } from "gatsby";
import { ProjectsQuery } from "../types";
import { Fade, Typography } from "@mui/material";
import Image from "./Image";
import Chips from "./Chips";
import VideoAnimation from "./VideoAnimation";
import useWidth from "../hooks/useWidth";

export type ProjectTileProps = {
  projectSlug: string;
  shouldHaveAnimationOnHover?: boolean;
};

const ProjectTile: FC<ProjectTileProps> = ({
  projectSlug,
  shouldHaveAnimationOnHover,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const width = useWidth();
  const isMobile = width === "xs" || width === "sm";

  useEffect(() => {
    setIsHovering(isMobile);
  }, [isMobile]);

  const {
    allJson: { edges },
  }: ProjectsQuery = useStaticQuery(graphql`
    query {
      allJson {
        edges {
          node {
            title
            titlePrefix
            industry
            slug
            url
            description
            shortDescription
            image
            video
            tags
          }
        }
      }
    }
  `);

  const project = edges.find((edge) => edge.node.slug === projectSlug)?.node;
  if (!project) return null;

  return (
    <Link to={`/projects/${project.url}`}>
      <Box
        sx={{
          width: "100%",
        }}
        onMouseEnter={() => shouldHaveAnimationOnHover && setIsHovering(true)}
        onMouseLeave={() => shouldHaveAnimationOnHover && setIsHovering(false)}
      >
        <Box
          sx={{
            height: "90%",
            overflow: "hidden",
            ".project-tile-image img, & video": {
              transition: "all .25s ease",
            },
            ".project-tile-image img:hover": {
              transform: "scale(1.1)",
            },
          }}
        >
          {project.video && (
            <VideoAnimation
              largeScreenVideo={project.video}
              smallScreenVideo={project.video}
              videoPlaceholder={project.image}
              fullHeight
            />
          )}
          {project.image && !project.video && (
            <Image
              style={{ height: "100%" }}
              imgStyle={{ objectPosition: "center" }}
              objectPosition="center"
              name={project.image}
              alt={project.title}
              className="project-tile-image"
            />
          )}
        </Box>
        <Stack
          sx={{
            justifyContent: "flex-end",
            alignItems: "flex-start",
            color: "text.primary",
            mt: 1.5,
            gap: 1.5,
          }}
        >
          <Stack
            direction="row"
            alignItems="space-between"
            justifyContent="center"
            width="100%"
            gap={2}
          >
            <Box flex={1} position="relative">
              <Box
                sx={{
                  display:
                    isMobile && shouldHaveAnimationOnHover ? "none" : "block",
                  position:
                    isMobile && shouldHaveAnimationOnHover
                      ? "absolute"
                      : "static",
                }}
              >
                <Fade in={!isHovering || !shouldHaveAnimationOnHover}>
                  <Typography variant="body1">
                    {project?.titlePrefix ? `${project?.titlePrefix}: ` : ""}
                    {project?.title}
                    {!isMobile && (
                      <Typography
                        component="span"
                        variant="body1"
                        color="text.secondary"
                      >
                        {` | ${project?.industry}`}
                      </Typography>
                    )}
                  </Typography>
                </Fade>
              </Box>
              <Box
                sx={{
                  display: !shouldHaveAnimationOnHover ? "none" : "block",
                  position:
                    shouldHaveAnimationOnHover && !isMobile
                      ? "absolute"
                      : "static",
                }}
                top={0}
              >
                <Fade in={isHovering}>
                  <Typography variant="body1" mb={0.5}>
                    {project?.shortDescription}
                  </Typography>
                </Fade>
              </Box>
            </Box>
          </Stack>
          {!shouldHaveAnimationOnHover && <Chips tags={project.tags} />}
        </Stack>
      </Box>
    </Link>
  );
};

export default ProjectTile;
